import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useToast } from '@/context/ToastContext'

const TYPE_COLORS: Record<string, string> = {
  success: 'var(--green, #22c55e)',
  error: 'var(--red, #ef4444)',
  warning: 'var(--accent)',
  info: 'var(--text2)',
}

// Toasts must paint ABOVE an open native <dialog>. showModal() promotes the dialog to the
// browser TOP LAYER, above ALL normal-flow content regardless of z-index - so a toast portaled
// to document.body (even at z-index 99999) renders behind the modal AND is sampled into its
// blurred ::backdrop. A `popover` doesn't help: a manual popover loses to a modal dialog (they
// occupy different top-layer tiers), and a second showModal() would freeze the app modal
// beneath it. Both were verified in a real browser.
//
// The fix (same trick as Tooltip.tsx): portal the toast INTO the topmost open <dialog> when
// one exists. A fixed-positioned child of the dialog lives in that same top layer, above the
// dialog's own ::backdrop, so it renders sharp and on top. Fall back to document.body when no
// modal is open (ordinary page-level toasts). Re-resolve the target on every modal open/close.
function useToastPortalTarget(): HTMLElement {
  const [target, setTarget] = useState<HTMLElement>(() => document.body)

  useEffect(() => {
    const resolve = () => {
      const dialogs = document.querySelectorAll<HTMLDialogElement>('dialog[open]')
      // Last open dialog = topmost (top-layer order is showModal() order = DOM/open order here,
      // since call sites mount dialogs in nesting order).
      const top = dialogs.length ? dialogs[dialogs.length - 1] : null
      setTarget(top ?? document.body)
    }
    resolve()
    // data-modal-count flips on every Modal open/close (Modal.tsx maintains it).
    const obs = new MutationObserver(resolve)
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-modal-count'] })
    return () => obs.disconnect()
  }, [])

  return target
}

/**
 * `placement` exists because the two shells put different things where a toast lands. The portal's
 * topbar is 56px tall, so `top: 72` clears it; the operator console's strip is 34px and its page
 * actions (Refresh, and the buttons beside it) sit exactly there - a toast over the control you
 * just used is worse than one anywhere else. So the console asks for the bottom corner, which is
 * empty in both shells but only matters in one.
 */
export function ToastContainer({ placement = 'top' }: { placement?: 'top' | 'bottom' }) {
  const { toasts, dismiss } = useToast()
  const target = useToastPortalTarget()

  if (toasts.length === 0) return null

  return createPortal(
    <div style={{
      position: 'fixed',
      ...(placement === 'top' ? { top: 72 } : { bottom: 24 }),
      right: 24,
      display: 'flex',
      // Newest nearest the edge it grew from, so a stack reads in the order it arrived.
      flexDirection: placement === 'top' ? 'column' : 'column-reverse',
      gap: 8,
      zIndex: 99999, pointerEvents: 'none',
    }}>
      {toasts.map((t) => (
        <div key={t.id}
          data-testid="toast"
          data-toast-type={t.type}
          role="status"
          aria-live={t.type === 'error' ? 'assertive' : 'polite'}
          style={{
          background: 'var(--bg2)',
          border: `1px solid ${TYPE_COLORS[t.type] ?? 'var(--border)'}`,
          borderRadius: 8,
          padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          fontFamily: 'var(--body)', fontSize: '0.82rem', color: 'var(--text)',
          maxWidth: 360, pointerEvents: 'all',
        }}>
          <span style={{ flex: 1 }}>{t.message}</span>
          <button
            onClick={() => dismiss(t.id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text3)', fontSize: '0.9rem', lineHeight: 1, padding: 0 }}
            aria-label="Dismiss"
          >×</button>
        </div>
      ))}
    </div>,
    target,
  )
}
